/**
 * Pictogramme d'un article, déduit de sa désignation.
 *
 * Rien n'est stocké ni saisi : le vendeur tape « Vélo course Motobécane » et
 * l'écran affiche 🚲. L'intérêt est la lecture en diagonale — sur une liste de
 * 400 lignes, l'œil trouve le vélo avant d'avoir lu le mot.
 *
 * Les motifs sont testés dans l'ordre : les plus spécifiques d'abord, sinon
 * « table de nuit » sortirait une table à manger et « eau de toilette » un WC.
 */
import { FAMILLES, motsSignificatifs } from './categories.js';
/** [motif recherché dans la désignation, pictogramme]. Ordre = priorité. */
const MOTIFS = [
    // — Multimédia, jeux, musique —
    ['SUPER NINTENDO', '🎮'], ['NINTENDO', '🎮'], ['PLAYSTATION', '🎮'], ['MEGADRIVE', '🎮'],
    ['XBOX', '🎮'], ['CONSOLE', '🎮'], ['MANETTE', '🎮'], ['GAME BOY', '🎮'], ['AMSTRAD', '🕹️'],
    ['JEU VIDEO', '🎮'], ['BLU RAY', '📀'], ['BLURAY', '📀'], ['DVD', '📀'], ['CD ', '💿'],
    ['33T', '🎵'], ['45T', '🎵'], ['VINYLE', '🎵'], ['DISQUE', '🎵'], ['CASSETTE', '📼'],
    ['GUITARE', '🎸'], ['PIANO', '🎹'], ['VIOLON', '🎻'], ['TROMPETTE', '🎺'],
    ['BATTERIE', '🥁'], ['ENCEINTE', '🔊'], ['AMPLI', '🔊'], ['CHAINE HIFI', '📻'],
    ['RADIO', '📻'], ['TOURNE DISQUE', '🎶'], ['PLATINE', '🎶'],
    ['LIVRE', '📚'], ['BD', '📚'], ['ROMAN', '📖'], ['PUZZLE', '🧩'], ['JOUET', '🧸'],
    ['PELUCHE', '🧸'], ['POUPEE', '🪆'], ['LEGO', '🧱'], ['JEU DE SOCIETE', '🎲'],
    // — Image et son —
    ['APPAREIL PHOTO', '📷'], ['ROLLEIFLEX', '📷'], ['LEICA', '📷'], ['POLAROID', '📷'],
    ['CAMERA', '🎥'], ['OBJECTIF', '📷'], ['JUMELLE', '🔭'], ['TELESCOPE', '🔭'],
    ['TELEVISEUR', '📺'], ['TELEVISION', '📺'], ['ORDINATEUR', '💻'], ['CLAVIER', '⌨️'],
    ['TELEPHONE', '📞'], ['GSM', '📱'], ['TABLETTE', '📱'],
    // — Mobilier —
    ['TABLE DE NUIT', '🛏️'], ['TABLE BASSE', '🪑'], ['TABLE', '🪵'], ['CHAISE', '🪑'],
    ['FAUTEUIL', '🛋️'], ['CANAPE', '🛋️'], ['TABOURET', '🪑'], ['BANC', '🪑'],
    ['ARMOIRE', '🚪'], ['COMMODE', '🗄️'], ['BUFFET', '🗄️'], ['ENFILADE', '🗄️'],
    ['VAISSELIER', '🗄️'], ['SECRETAIRE', '🗄️'], ['BUREAU', '🗄️'], ['ETAGERE', '🪜'],
    ['BIBLIOTHEQUE', '📚'], ['GUERIDON', '🪵'], ['LIT ', '🛏️'], ['MATELAS', '🛏️'],
    ['MEUBLE', '🗄️'], ['MALLE', '🧳'], ['VALISE', '🧳'], ['COFFRE', '📦'],
    // — Décoration —
    ['LAMPE', '💡'], ['LUSTRE', '💡'], ['SUSPENSION', '💡'], ['APPLIQUE', '💡'],
    ['ABAT JOUR', '💡'], ['BOUGIE', '🕯️'], ['BOUGEOIR', '🕯️'], ['PHOTOPHORE', '🕯️'],
    ['MIROIR', '🪞'], ['CADRE', '🖼️'], ['ENCADREMENT', '🖼️'], ['TABLEAU', '🖼️'],
    ['AFFICHE', '🖼️'], ['STATUE', '🗿'], ['STATUETTE', '🗿'], ['BUSTE', '🗿'],
    ['VASE', '🏺'], ['POTERIE', '🏺'], ['PENDULE', '🕰️'], ['HORLOGE', '🕰️'],
    ['REVEIL', '⏰'], ['MONTRE', '⌚'], ['TAPIS', '🧶'], ['RIDEAU', '🪟'],
    ['COUSSIN', '🛋️'], ['PLAID', '🧣'], ['CAGE A OISEAU', '🐦'], ['CAGE', '🐦'],
    ['PLANTE', '🪴'], ['POT DE FLEUR', '🪴'],
    // — Art de la table & cuisine —
    ['SERVICE', '🍽️'], ['ASSIETTE', '🍽️'], ['PLAT', '🍽️'], ['SOUPIERE', '🍲'],
    ['SAUCIERE', '🍲'], ['MENAGERE', '🍴'], ['COUVERT', '🍴'], ['FOURCHETTE', '🍴'],
    ['CARAFE', '🍶'], ['VERRE', '🥃'], ['CRISTAL', '🥃'], ['TASSE', '☕'],
    ['THEIERE', '🫖'], ['CAFETIERE', '☕'], ['CASSEROLE', '🍳'], ['POELE', '🍳'],
    ['COCOTTE', '🍲'], ['MIXEUR', '🔌'], ['ROBOT', '🔌'], ['MICRO ONDE', '🔌'],
    // — Sport & loisirs —
    ['VELO', '🚲'], ['BICYCLETTE', '🚲'], ['TROTTINETTE', '🛴'], ['SKI', '🎿'],
    ['SNOWBOARD', '🏂'], ['LUGE', '🛷'], ['PATIN', '⛸️'], ['ROLLER', '🛼'],
    ['RAQUETTE', '🎾'], ['TENNIS', '🎾'], ['BALLON', '⚽'], ['FOOT', '⚽'],
    ['BASKET', '🏀'], ['GOLF', '⛳'], ['PECHE', '🎣'], ['CANNE', '🎣'],
    ['HALTERE', '🏋️'], ['VELO ELLIPTIQUE', '🏋️'], ['TENTE', '⛺'], ['SAC DE COUCHAGE', '🛌'],
    ['SURF', '🏄'], ['PLONGEE', '🤿'], ['CASQUE', '🪖'], ['POUSSETTE', '🍼'],
    // — Luxe, mode, bijoux —
    ['VUITTON', '👜'], ['CHANEL', '👜'], ['HERMES', '👜'], ['GUCCI', '👜'],
    ['SAC A MAIN', '👜'], ['SAC ', '👜'], ['PORTEFEUILLE', '👛'], ['CEINTURE', '🩹'],
    ['BRACELET', '📿'], ['COLLIER', '📿'], ['BAGUE', '💍'], ['BOUCLE', '💎'],
    ['BIJOU', '💎'], ['DIAMANT', '💎'], ['PERLE', '📿'], ['LUNETTE', '👓'],
    ['MANTEAU', '🧥'], ['VESTE', '🧥'], ['ROBE', '👗'], ['CHAUSSURE', '👞'],
    ['BOTTE', '👢'], ['CHAPEAU', '👒'], ['ECHARPE', '🧣'], ['FOULARD', '🧣'],
    // — Hygiène, entretien, droguerie —
    ['SHAMPOING', '🧴'], ['GEL DOUCHE', '🧴'], ['SAVON', '🧼'], ['LESSIVE', '🧺'],
    ['LIQUIDE VAISSELLE', '🧽'], ['WOOLITE', '🧺'], ['DENTIFRICE', '🪥'],
    ['PARODONTAX', '🪥'], ['KUKIDENT', '🦷'], ['DENTIER', '🦷'], ['BROSSE A DENT', '🪥'],
    ['RASOIR', '🪒'], ['GEL A RASER', '🪒'], ['APRES RASAGE', '🪒'], ['DEODORANT', '🧴'],
    ['CREME', '🧴'], ['EAU DE TOILETTE', '🌸'], ['EAU DE PARFUM', '🌸'], ['PARFUM', '🌸'],
    ['MAQUILLAGE', '💄'], ['PALETTE', '💄'], ['ROUGE A LEVRE', '💄'], ['VERNIS', '💅'],
    ['CISEAU', '✂️'], ['PINCETTE', '✂️'],
    // — Outillage, bricolage, divers —
    ['MULTIPRISE', '🔌'], ['RALLONGE', '🔌'], ['CHARGEUR', '🔌'], ['PILE', '🔋'],
    ['AMPOULE', '💡'], ['SANGLE', '🪢'], ['CORDE', '🪢'], ['GANT', '🧤'],
    ['PERCEUSE', '🔩'], ['MARTEAU', '🔨'], ['TOURNEVIS', '🪛'], ['SCIE', '🪚'],
    ['ECHELLE', '🪜'], ['OUTIL', '🧰'], ['CAISSE A OUTIL', '🧰'], ['TONDEUSE', '🌱'],
    ['ARROSOIR', '🚿'], ['BARBECUE', '🍖'], ['MACHINE A ECRIRE', '⌨️'],
    ['MACHINE A COUDRE', '🧵'], ['TISSU', '🧵'], ['LAINE', '🧶'],
    ['PIECE DE MONNAIE', '🪙'], ['MEDAILLE', '🎖️'], ['TIMBRE', '📮'], ['CARTE POSTALE', '📮'],
];
/** Pictogramme de repli quand rien ne correspond — celui de la famille. */
const PAR_CATEGORIE = Object.fromEntries(FAMILLES.map((f) => [f.libelle, f.emoji]));
const PAR_DEFAUT = '🏷️';
/** Retire les accents et met en majuscules, pour comparer sans surprise. */
function normaliser(texte) {
    return texte
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toUpperCase();
}
/**
 * Pictogramme correspondant à une désignation. La catégorie sert de repli,
 * si bien qu'un article obscur reçoit au moins l'icône de sa famille.
 */
export function emojiArticle(designation, categorie) {
    const texte = ` ${normaliser(designation)} `;
    for (const [motif, emoji] of MOTIFS) {
        if (texte.includes(motif))
            return emoji;
    }
    // Deuxième passe sur les mots seuls : « CHAISES » contient « CHAISE », mais
    // « LAMPES » au pluriel a déjà été pris ; ceci rattrape les formes isolées.
    const mots = motsSignificatifs(designation);
    for (const [motif, emoji] of MOTIFS) {
        if (!motif.includes(' ') && mots.some((mot) => mot.startsWith(motif)))
            return emoji;
    }
    return (categorie && PAR_CATEGORIE[categorie]) || PAR_DEFAUT;
}
//# sourceMappingURL=emojis.js.map